import dayjs, { Dayjs } from 'dayjs';
import { PeriodBreakdownItem, StatsWeekTraining, WeekBreakdownItem } from '../../../../models/stats.models';
import {
  getPeriodStatus,
  isCurrentWeek,
  parseApiDateToLocal,
} from '../../../../components/period-summary/period-summary.helpers';
import {
  getTeamCardTheme,
  getTeamInitials,
} from '../../../../components/team-attendance-breakdown/team-attendance-breakdown.helpers';
import { getMeasurementUnit } from '../../../../helpers/general.helpers';
import { formatSeasonDurationHours, getFocusColor, getFocusLabel } from '../../training-log.helpers';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export type WeekCardStatus = 'current' | 'future' | 'past';
export type DayRowState = 'rest' | 'today' | 'future' | 'training';

export interface DaySlot {
  dayLabel: string;
  dayOffset: number;
  date: Dayjs;
  trainings: StatsWeekTraining[];
  state: DayRowState;
}

const INDIVIDUAL_TEAM_CHIP_KEY = 'individual';

const INDIVIDUAL_TEAM_CHIP_THEME = {
  cardBg: '#f3f4f6',
  avatarBg: '#9ca3af',
  ringColor: '#9ca3af',
  ringTrack: '#f3f4f6',
};

export interface WeekTeamChip {
  key: string;
  teamName: string;
  count: number;
  initials: string;
  theme: ReturnType<typeof getTeamCardTheme>;
}

export interface WeekComparisonMetric {
  key: string;
  label: string;
  percentDiff: number | null;
  currentDisplay: string;
  previousDisplay: string;
  direction: 'up' | 'down' | 'flat';
  trend: 'up' | 'down' | 'flat';
}

export const getDefaultPeriodIndex = (periods: PeriodBreakdownItem[], weeks: WeekBreakdownItem[]): number => {
  const current = periods.find((p) => getPeriodStatus(p, weeks) === 'current');
  if (current) {
    return current.index;
  }

  const withData = [...periods].reverse().find((p) => (p.duration?.total ?? 0) > 0);
  if (withData) {
    return withData.index;
  }

  return periods[0]?.index ?? 1;
};

export const getPreviousWeek = (weeks: WeekBreakdownItem[], week: WeekBreakdownItem): WeekBreakdownItem | undefined => {
  if (week.index <= 1) {
    return undefined;
  }
  return weeks.find((w) => w.index === week.index - 1);
};

export const getWeekCardStatus = (week: WeekBreakdownItem): WeekCardStatus => {
  if (isCurrentWeek(week.week)) {
    return 'current';
  }

  const weekStart = parseApiDateToLocal(week.week);
  if (weekStart.isValid() && weekStart.isAfter(dayjs(), 'day') && (week.trainings?.total ?? 0) === 0) {
    return 'future';
  }

  return 'past';
};

export const getDayRowState = (
  weekStart: string,
  dayOffset: number,
  hasTrainings: boolean,
  weekStatus: WeekCardStatus
): DayRowState => {
  const date = parseApiDateToLocal(weekStart).startOf('day').add(dayOffset, 'day');
  const today = dayjs().startOf('day');

  if (date.isSame(today, 'day')) {
    return 'today';
  }

  if (date.isAfter(today, 'day')) {
    return weekStatus === 'future' || weekStatus === 'current' ? 'future' : 'rest';
  }

  return hasTrainings ? 'training' : 'rest';
};

export const groupTrainingsByDay = (
  weekStart: string,
  trainings: StatsWeekTraining[],
  weekStatus: WeekCardStatus
): DaySlot[] => {
  const start = parseApiDateToLocal(weekStart).startOf('day');

  return DAY_LABELS.map((dayLabel, dayOffset) => {
    const date = start.add(dayOffset, 'day');
    const dayTrainings = (trainings ?? []).filter((t) => {
      const trainingDate = parseApiDateToLocal(t.time);
      return trainingDate.isValid() && trainingDate.isSame(date, 'day');
    });
    const state = getDayRowState(weekStart, dayOffset, dayTrainings.length > 0, weekStatus);

    return {
      dayLabel,
      dayOffset,
      date,
      trainings: dayTrainings,
      state,
    };
  });
};

export const buildWeekTeamChips = (trainings: StatsWeekTraining[]): WeekTeamChip[] => {
  const aggregates = new Map<string, { teamName: string; color: string | null; count: number }>();

  (trainings ?? []).forEach((t) => {
    const isIndividual = !t.team?.id && !t.team?.name?.trim();
    const key = isIndividual ? INDIVIDUAL_TEAM_CHIP_KEY : String(t.team?.id ?? t.team?.name?.trim());
    const teamName = isIndividual ? 'Individual' : t.team?.name?.trim() || 'Unknown Team';

    const existing = aggregates.get(key);
    if (existing) {
      existing.count += 1;
      if (!existing.color && t.team?.color) {
        existing.color = t.team.color;
      }
    } else {
      aggregates.set(key, {
        teamName,
        color: t.team?.color ?? null,
        count: 1,
      });
    }
  });

  return Array.from(aggregates.entries()).map(([key, { teamName, color, count }], index) => ({
    key,
    teamName,
    count,
    initials: key === INDIVIDUAL_TEAM_CHIP_KEY ? 'IN' : getTeamInitials(teamName),
    theme: key === INDIVIDUAL_TEAM_CHIP_KEY ? INDIVIDUAL_TEAM_CHIP_THEME : getTeamCardTheme(color ?? undefined, index),
  }));
};

export const formatTrainingTime = (time: string): string => {
  const parsed = parseApiDateToLocal(time);
  return parsed.isValid() ? parsed.format('h:mma') : time;
};

export const formatEffortDisplay = (average: number | undefined): string => {
  if (average === undefined || !Number.isFinite(average) || average <= 0) {
    return '—';
  }
  const rounded = Math.round(average);
  return `${rounded}/10`;
};

export const formatEffortShort = (average: number | undefined): string => {
  if (average === undefined || !Number.isFinite(average) || average <= 0) {
    return '—';
  }
  const rounded = Math.round(average * 10) / 10;
  return `${rounded}`;
};

export const getRpeDotColor = (rating: number | null): string => {
  if (rating === null || rating <= 0) {
    return '#9CA3AF';
  }
  if (rating <= 3) {
    return '#16A34A';
  }
  if (rating <= 5) {
    return '#B8860B';
  }
  if (rating <= 7) {
    return '#EA580C';
  }
  return '#DC2626';
};

const LOG_FOCUS_BADGE_STYLES: Record<string, { background: string; color: string }> = {
  Distance: { background: '#DBEAFE', color: '#2563EB' },
  Intensity: { background: '#FEE2E2', color: '#DC2626' },
  Recovery: { background: '#DCFCE7', color: '#16A34A' },
  Overdistance: { background: '#E0E7FF', color: '#4338CA' },
  Race: { background: '#FEF3C7', color: '#D97706' },
  Speed: { background: '#FEF9C3', color: '#CA8A04' },
  Pace: { background: '#FCE7F3', color: '#DB2777' },
  Other: { background: '#F3F4F6', color: '#6B7280' },
};

export const getFocusBadgeStyle = (focus: string): { background: string; color: string } => {
  const label = getFocusLabel(focus);
  if (LOG_FOCUS_BADGE_STYLES[label]) {
    return LOG_FOCUS_BADGE_STYLES[label];
  }

  const color = getFocusColor(focus);
  return {
    background: `${color}33`,
    color,
  };
};

const getChangeDirection = (percentDiff: number | null | undefined): 'up' | 'down' | 'flat' => {
  if (percentDiff === null || percentDiff === undefined || !Number.isFinite(percentDiff)) {
    return 'flat';
  }
  if (percentDiff === 0) {
    return 'flat';
  }
  return percentDiff > 0 ? 'up' : 'down';
};

const getColorTrend = (direction: 'up' | 'down' | 'flat', lowerIsBetter = false): 'up' | 'down' | 'flat' => {
  if (direction === 'flat' || !lowerIsBetter) {
    return direction;
  }
  return direction === 'up' ? 'down' : 'up';
};

const buildComparisonMetricTrend = (
  percentDiff: number | null | undefined,
  lowerIsBetter = false
): Pick<WeekComparisonMetric, 'direction' | 'trend'> => {
  const direction = getChangeDirection(percentDiff);
  return {
    direction,
    trend: getColorTrend(direction, lowerIsBetter),
  };
};

const formatPctChange = (percentDiff: number | null | undefined): string => {
  if (percentDiff === null || percentDiff === undefined || !Number.isFinite(percentDiff)) {
    return '— 0%';
  }
  if (percentDiff === 0) {
    return '— 0%';
  }
  const sign = percentDiff > 0 ? '+' : '';
  return `${sign}${Math.round(percentDiff)}%`;
};

export const buildWeekComparisonMetrics = (
  current: WeekBreakdownItem,
  previous: WeekBreakdownItem | undefined
): WeekComparisonMetric[] => {
  const prevDuration = previous?.duration?.total ?? 0;
  const prevDistance = previous?.distance?.total ?? 0;
  const prevSessions = previous?.trainings?.total ?? 0;
  const prevEffort = previous?.effortRatings?.average ?? 0;
  const distanceUnit = getMeasurementUnit();

  return [
    {
      key: 'duration',
      label: 'vs last week',
      percentDiff: current.duration?.percentDiff ?? null,
      currentDisplay: formatSeasonDurationHours(current.duration?.total ?? 0),
      previousDisplay: formatSeasonDurationHours(prevDuration),
      ...buildComparisonMetricTrend(current.duration?.percentDiff),
    },
    {
      key: 'distance',
      label: 'vs last week',
      percentDiff: current.distance?.percentDiff ?? null,
      currentDisplay: `${Math.round(current.distance?.total ?? 0)}${distanceUnit}`,
      previousDisplay: `${Math.round(prevDistance)}${distanceUnit}`,
      ...buildComparisonMetricTrend(current.distance?.percentDiff),
    },
    {
      key: 'sessions',
      label: 'vs last week',
      percentDiff: current.trainings?.percentDiff ?? null,
      currentDisplay: `${current.trainings?.total ?? 0} sessions`,
      previousDisplay: `${prevSessions} sessions`,
      ...buildComparisonMetricTrend(current.trainings?.percentDiff),
    },
    {
      key: 'effort',
      label: 'vs last week',
      percentDiff: current.effortRatings?.percentDiff ?? null,
      currentDisplay: formatEffortDisplay(current.effortRatings?.average),
      previousDisplay: formatEffortDisplay(prevEffort),
      ...buildComparisonMetricTrend(current.effortRatings?.percentDiff, true),
    },
  ];
};

export const formatComparisonChange = (metric: WeekComparisonMetric): string => {
  return formatPctChange(metric.percentDiff);
};

export const getTrainingSchedulePath = (
  trainingId: string | number,
  trainingType: 'athlete' | 'coach' | 'guardian' = 'athlete'
): string => `/home?trainingId=${Number(trainingId)}&trainingType=${trainingType}`;

export const formatSeasonHoursHeader = (seconds: number): string => formatSeasonDurationHours(seconds);
